import React, { useState, useEffect } from 'react';
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { LanguageProvider } from './context/LanguageContext';
import { AnimatePresence } from 'framer-motion';
import Header from './components/Header';
import HeroSection from './components/HeroSection';
import IntroSection from './components/IntroSection';
import RealityCheckSection from './components/RealityCheckSection';
import WhySection from './components/WhySection';
import AudienceSection from './components/AudienceSection';
import ServicesSection from './components/ServicesSection';
import RoadFramework from './components/RoadFramework';
import HowWeWorkSection from './components/HowWeWorkSection';
import CommunitySection from './components/CommunitySection';
import AboutSection from './components/AboutSection';
import BlogSection from './components/BlogSection';
import FAQSection from './components/FAQSection';
import BookingSection from './components/BookingSection';
import MasterclassBanner from './components/MasterclassBanner';
import Footer from './components/Footer';
import ImpressumPage from './components/ImpressumPage';
import DatenschutzPage from './components/DatenschutzPage';
import AGBPage from './components/AGBPage';
import CookieConsent from './components/CookieConsent';
import AdminPanel from './components/AdminPanel';
import CRMBoard from './components/CRMBoard';
import JohannaPage from './components/JohannaPage';
import CustomerPortal from './components/CustomerPortal';
import EmployeeSessionPage from './components/EmployeeSessionPage';
import ResetPassword from './components/ResetPassword';
import NewsletterSignup from './components/NewsletterSignup';
import NewsletterConfirm from './components/NewsletterConfirm';
import ChatWidget from './components/ChatWidget';
import PillarStationaererHandel from './components/pillar/PillarStationaererHandel';
import PillarFuerFilialisten from './components/pillar/PillarFuerFilialisten';
import PillarStrategieRetail from './components/pillar/PillarStrategieRetail';
import PillarBeratungKassel from './components/pillar/PillarBeratungKassel';
import LandingReadyCheck from './components/landing/LandingReadyCheck';
import LandingStrategieWorkshop from './components/landing/LandingStrategieWorkshop';
import LandingKombi from './components/landing/LandingKombi';
import LandingMasterclass from './components/landing/LandingMasterclass';
import NewsletterArticle from './components/NewsletterArticle';
import CheckoutSuccess from './components/CheckoutSuccess';
import CheckoutCancelled from './components/CheckoutCancelled';
import { usePageTracking, initGoogleAnalytics } from './hooks/useAnalytics';

function HomePage() {
  const [showImpressum, setShowImpressum] = useState(false);
  const [showDatenschutz, setShowDatenschutz] = useState(false);
  const [showAGB, setShowAGB] = useState(false);
  usePageTracking();

  useEffect(() => {
    const ds = () => setShowDatenschutz(true);
    const agb = () => setShowAGB(true);
    window.addEventListener('openDatenschutz', ds);
    window.addEventListener('openAGB', agb);
    return () => {
      window.removeEventListener('openDatenschutz', ds);
      window.removeEventListener('openAGB', agb);
    };
  }, []);

  return (
    <>
      <Header />
      <main>
        <HeroSection />
        <IntroSection />
        <RealityCheckSection />
        <WhySection />
        <AudienceSection />
        <RoadFramework />
        <ServicesSection />
        <MasterclassBanner />
        <HowWeWorkSection />
        <CommunitySection />
        <AboutSection />
        <BlogSection />
        <FAQSection />
        <BookingSection />
        <NewsletterSignup />
      </main>
      <Footer
        onImpressum={() => setShowImpressum(true)}
        onDatenschutz={() => setShowDatenschutz(true)}
        onAGB={() => setShowAGB(true)}
      />
      <CookieConsent onPrivacy={() => setShowDatenschutz(true)} />
      <ChatWidget />
      <AnimatePresence>
        {showImpressum && <ImpressumPage onClose={() => setShowImpressum(false)} />}
        {showDatenschutz && <DatenschutzPage onClose={() => setShowDatenschutz(false)} />}
        {showAGB && <AGBPage onClose={() => setShowAGB(false)} />}
      </AnimatePresence>
    </>
  );
}

function ScrollToTop() {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    if (hash) {
      const el = document.getElementById(hash.slice(1));
      if (el) { el.scrollIntoView({ behavior: 'smooth', block: 'start' }); return; }
    }
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
  }, [pathname, hash]);
  return null;
}

function App() {
  useEffect(() => { initGoogleAnalytics(); }, []);
  return (
    <HelmetProvider>
      <LanguageProvider>
        <div className="App" data-testid="app-root">
          <BrowserRouter>
            <ScrollToTop />
            <Routes>
              <Route path="/" element={<HomePage />} />
              <Route path="/ki-im-stationaeren-handel" element={<PillarStationaererHandel />} />
              <Route path="/ki-fuer-filialisten" element={<PillarFuerFilialisten />} />
              <Route path="/ki-strategie-retail" element={<PillarStrategieRetail />} />
              <Route path="/ki-beratung-kassel" element={<PillarBeratungKassel />} />
              <Route path="/ki-ready-check" element={<LandingReadyCheck />} />
              <Route path="/ki-strategie-workshop" element={<LandingStrategieWorkshop />} />
              <Route path="/ki-kombi-paket" element={<LandingKombi />} />
              <Route path="/ki-masterclass" element={<LandingMasterclass />} />
              <Route path="/insights/:slug" element={<NewsletterArticle />} />
              <Route path="/checkout/success" element={<CheckoutSuccess />} />
              <Route path="/checkout/cancelled" element={<CheckoutCancelled />} />
              <Route path="/admin" element={<AdminPanel />} />
              <Route path="/crm" element={<CRMBoard />} />
              <Route path="/portal" element={<CustomerPortal />} />
              <Route path="/c/:token" element={<EmployeeSessionPage />} />
              <Route path="/johanna" element={<JohannaPage />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/newsletter-confirm" element={<NewsletterConfirm action="confirm" />} />
              <Route path="/newsletter-unsubscribe" element={<NewsletterConfirm action="unsubscribe" />} />
            </Routes>
          </BrowserRouter>
        </div>
      </LanguageProvider>
    </HelmetProvider>
  );
}

export default App;
